import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/motion";

const ServiceRow = ({ service, index }) => {
  const flip = index % 2 === 1;
  return (
    <Reveal delay={0.05}>
      <div
        className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center py-14 border-t border-[#282E3F]/70`}
        data-testid={`service-row-${index}`}
      >
        <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
          <div className="relative overflow-hidden rounded-xl border border-[#282E3F] group">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-64 sm:h-80 object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12]/70 to-transparent" />
          </div>
        </div>
        <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
          <p className="font-mono text-xs tracking-[0.3em] text-sky-400/90">SERVICE {String(index + 1).padStart(2, "0")}</p>
          <h3 className="mt-4 font-display font-semibold tracking-tight text-xl sm:text-2xl">{service.title}</h3>
          <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-xl">{service.description}</p>
          <Link
            to="/contact"
            data-testid={`service-enquire-${index}`}
            className="group mt-7 inline-flex items-center gap-2 text-sm font-medium text-sky-300 hover:text-sky-200 transition-colors"
          >
            Enquire on this mandate
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
};

const DivisionPage = ({ division, afterHero = null }) => (
  <div data-testid={`division-page-${division.number}`}>
    <section className="relative pt-44 pb-24 overflow-hidden grain">
      <img src={division.heroImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/80 to-[#0B0D12]/40" />
      <div className="absolute inset-0 hero-glow" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12">
        <MaskedLine delay={0.15}>
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-sky-400/90" data-testid="division-overline">
            Division {division.number} — Crestmont Enterprises
          </span>
        </MaskedLine>
        <h1 className="mt-6 font-display font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="division-title">
          <MaskedLine delay={0.3}>{division.title.split(" & ")[0]} &</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="gold-text">{division.title.split(" & ")[1]}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6} className="mt-6 max-w-2xl">
          <span className="text-base sm:text-lg text-slate-300 leading-relaxed" data-testid="division-tagline">{division.tagline}</span>
        </MaskedLine>
        <MaskedLine delay={0.75} className="mt-5 max-w-2xl">
          <span className="text-sm text-slate-400 leading-relaxed">{division.description}</span>
        </MaskedLine>
      </div>
    </section>

    {afterHero}

    <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-24" data-testid="division-services">
      <Reveal>
        <Overline>Core Services</Overline>
      </Reveal>
      {division.services.map((s, i) => (
        <ServiceRow key={s.title} service={s} index={i} />
      ))}
    </section>

    <section className="border-t border-[#282E3F]/60 py-20" data-testid="division-standard">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-7">
          <Overline>Divisional Standard</Overline>
          <p className="mt-5 font-display font-medium tracking-tight text-xl sm:text-2xl leading-snug text-slate-200">
            {division.standard}
          </p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 lg:justify-self-end">
          <Link
            to="/contact"
            data-testid="division-cta-btn"
            className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-sm px-8 py-4 rounded-full hover:shadow-[0_0_32px_rgba(59,130,246,0.45)] transition-shadow duration-300"
          >
            Request Institutional Briefing
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
);

export default DivisionPage;
